import type { GetMarketplaceJobsResponseDto } from "@workspace/dto/job/get-marketplace-jobs";
import { Button, buttonVariants } from "@workspace/ui/components/button";
import { useIsMobile } from "@workspace/ui/hooks/use-mobile";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTheme } from "next-themes";
import { useQueryStates } from "nuqs";
import {
  type Dispatch,
  type MouseEvent,
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import ReactMapGL, {
  type ErrorEvent,
  type MapProps,
  type MapRef,
  Marker,
  type ViewState,
  type ViewStateChangeEvent,
} from "react-map-gl";
import { toast } from "sonner";
import { Icons } from "@/components/icons";
import { Loading } from "@/components/loading";
import {
  SOUTH_AFRICA_BOUNDS,
  SOUTH_AFRICA_VIEW,
} from "@/components/maps/location-map-constants";
import { env } from "@/env.mjs";
import { useTrackJobImpression } from "@/lib/hooks/use-track-job-impression";
import { resolveMapboxMapError } from "@/lib/mapbox/map-error";
import { ClusterMarker } from "./cluster-marker";
import type { MapBounds } from "./map-bounds";
import { MarketplaceJobMarkerLabel } from "./marketplace-job-marker-label";
import { MarketplaceJobPopupResponsive } from "./marketplace-job-popup-responsive";
import { marketplaceSearchParamsParser } from "./marketplace-search-params";
import {
  type JobClusterFeature,
  type JobClusterLeaf,
  type JobClusterResult,
  type MarketplaceJob,
  useCluster,
} from "./use-cluster";

const MAX_MAP_ZOOM = 20;
const MAP_FIT_PADDING_MOBILE = 32;
const MAP_FIT_PADDING_DESKTOP = 60;
const MARKER_CENTER_TOLERANCE = 0.01;
const MARKER_OPEN_DELAY_MS = 100;
const URL_QUERY_OR_HASH = /[?#]/;
const MOBILE_LAYOUT_MAX_WIDTH = 767;

function isJobCluster(cluster: JobClusterResult): cluster is JobClusterFeature {
  return "cluster" in cluster.properties && cluster.properties.cluster === true;
}

function isJobLeaf(cluster: JobClusterResult): cluster is JobClusterLeaf {
  return !isJobCluster(cluster);
}

function hasViewportParams(
  query: Record<string, string | string[] | undefined>
) {
  return "latitude" in query || "longitude" in query || "zoom" in query;
}

function southAfricaFitPadding() {
  return window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH}px)`).matches
    ? MAP_FIT_PADDING_MOBILE
    : MAP_FIT_PADDING_DESKTOP;
}

function readMapBounds(el: RefObject<MapRef | null>): MapBounds | undefined {
  const map = el.current?.getMap();
  if (!map) {
    return;
  }

  const bounds = map.getBounds();
  if (!bounds) {
    return;
  }

  return [
    bounds.getWest(),
    bounds.getSouth(),
    bounds.getEast(),
    bounds.getNorth(),
  ];
}

interface ExtendedMapProps extends MapProps {
  jobs?: GetMarketplaceJobsResponseDto["jobs"];
  /**
   * Emitted every time the visible bounding box changes (on map load and on
   * each `moveend`). Consumed by `JobListingsLayout` to drive client-side
   * "jobs in view" filtering for the listings panel.
   */
  onBoundsChange?: (bounds: MapBounds) => void;
  /**
   * Emitted whenever the user's geolocation changes (initial
   * `getCurrentPosition` on load + the geolocate control). Forwards the value
   * up to the listings panel so the relocated AI "near me" filter input can
   * resolve coordinates after it moved out of the map overlay.
   */
  onUserLocationChange?: (location: {
    latitude?: number;
    longitude?: number;
  }) => void;
  /**
   * The currently selected job id. When set externally (list click) the map
   * opens the same popover a marker click would; when cleared, the popover
   * closes. When a marker is clicked, the map calls `onSelectedIdChange` so
   * the list highlights + scrolls to the matching row.
   */
  selectedId?: string | null;
  onSelectedIdChange?: (id: string | null) => void;
  /** The hovered job id (from the list) — highlights the matching marker. */
  hoveredId?: string | null;
  /** Emitted on marker hover; the list highlights the matching row. */
  onHoveredIdChange?: (id: string | null) => void;
  /**
   * The current user location. When set the map renders a pulsing dot
   * marker. Receives initial + geolocate-button updates from the parent.
   */
  userLocation?: UserLocationState | null;
}

interface UserLocationState extends Partial<ViewState> {
  latitude?: number;
  longitude?: number;
}

type OpenJobMarkerOptions = {
  markerId: string;
  longitude: number;
  latitude: number;
  zoom?: number;
  job?: MarketplaceJob;
  trackImpression?: boolean;
  /** Attribution source; defaults to `map_click`. List-initiated opens pass `list_click`. */
  impressionSource?: "map_click" | "list_click";
  /** When false, fly to the marker without opening the popover/drawer. */
  openDetail?: boolean;
};

type SelectedJobMarker = {
  markerId: string;
  longitude: number;
  latitude: number;
  job: MarketplaceJob;
};

type MapViewState = {
  mapBounds: [number, number, number, number] | undefined;
  mapZoom: number;
  openMarkerId: string | null;
  selectedJobMarker: SelectedJobMarker | null;
};

type MapViewAction =
  | { type: "mapBounds"; value: [number, number, number, number] | undefined }
  | { type: "mapZoom"; value: number }
  | { type: "openMarkerId"; value: string | null }
  | { type: "selectedJobMarker"; value: SelectedJobMarker | null }
  | { type: "closeMarker" };

function mapViewReducer(
  state: MapViewState,
  action: MapViewAction
): MapViewState {
  switch (action.type) {
    case "mapBounds":
      return { ...state, mapBounds: action.value };
    case "mapZoom":
      return { ...state, mapZoom: action.value };
    case "openMarkerId":
      return { ...state, openMarkerId: action.value };
    case "selectedJobMarker":
      return { ...state, selectedJobMarker: action.value };
    case "closeMarker":
      return { ...state, openMarkerId: null, selectedJobMarker: null };
    default:
      return state;
  }
}

function useMarketplaceMapErrorHandler(setLoaded: (loaded: boolean) => void) {
  return useCallback(
    (evt: ErrorEvent) => {
      setLoaded(true);

      const presentation = resolveMapboxMapError(
        evt.error,
        typeof navigator !== "undefined" && !navigator.onLine
      );

      if (!presentation) {
        return;
      }

      toast.error(presentation.title, {
        id: "marketplace-map-error",
        description: presentation.description,
      });
    },
    [setLoaded]
  );
}

function useOpenJobMarker(
  ref: RefObject<MapRef | null>,
  jobs: ExtendedMapProps["jobs"],
  trackMapClickImpression: ReturnType<typeof useTrackJobImpression>,
  {
    dispatch,
    isCenteringRef,
    trackListClickImpression,
  }: {
    dispatch: Dispatch<MapViewAction>;
    isCenteringRef: RefObject<boolean>;
    trackListClickImpression?: ReturnType<typeof useTrackJobImpression>;
  }
) {
  const flyToCleanupRef = useRef<(() => void) | null>(null);
  const openDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingOpen = useCallback(() => {
    flyToCleanupRef.current?.();
    flyToCleanupRef.current = null;
    if (openDelayRef.current !== null) {
      clearTimeout(openDelayRef.current);
      openDelayRef.current = null;
    }
    const el = ref.current;
    if (el) {
      el.getMap().stop();
    }
    isCenteringRef.current = false;
  }, [ref, isCenteringRef]);

  return useCallback(
    ({
      markerId,
      longitude,
      latitude,
      zoom,
      job,
      trackImpression = false,
      impressionSource,
      openDetail = true,
    }: OpenJobMarkerOptions) => {
      cancelPendingOpen();

      const el = ref.current;
      if (!el) {
        return;
      }

      const clickedJob = job ?? jobs?.find((item) => item.id === markerId);
      if (trackImpression && clickedJob) {
        const tracker =
          impressionSource === "list_click" && trackListClickImpression
            ? trackListClickImpression
            : trackMapClickImpression;
        tracker(
          clickedJob.id,
          clickedJob.organizationId,
          clickedJob.storeId ?? undefined
        );
      }

      dispatch({
        type: "selectedJobMarker",
        value: clickedJob
          ? {
              markerId,
              longitude,
              latitude,
              job: clickedJob,
            }
          : null,
      });

      const map = el.getMap();
      const targetZoom = Math.min(
        MAX_MAP_ZOOM,
        Math.max(map.getZoom(), zoom ?? MAX_MAP_ZOOM)
      );

      const revealMarker = () => {
        if (!openDetail) {
          isCenteringRef.current = false;
          return;
        }
        isCenteringRef.current = true;
        dispatch({ type: "openMarkerId", value: null });
        openDelayRef.current = setTimeout(() => {
          dispatch({ type: "openMarkerId", value: markerId });
          isCenteringRef.current = false;
          openDelayRef.current = null;
        }, MARKER_OPEN_DELAY_MS);
      };

      const isAlreadyAtTarget =
        Math.abs(map.getCenter().lng - longitude) < MARKER_CENTER_TOLERANCE &&
        Math.abs(map.getCenter().lat - latitude) < MARKER_CENTER_TOLERANCE &&
        Math.abs(map.getZoom() - targetZoom) < 0.01;

      if (isAlreadyAtTarget) {
        revealMarker();
        return;
      }

      isCenteringRef.current = true;
      dispatch({ type: "openMarkerId", value: null });

      const onMoveEnd = () => {
        map.off("moveend", onMoveEnd);
        flyToCleanupRef.current = null;
        if (openDetail) {
          revealMarker();
        } else {
          isCenteringRef.current = false;
        }
      };

      flyToCleanupRef.current = () => {
        map.off("moveend", onMoveEnd);
      };

      map.on("moveend", onMoveEnd);
      map.flyTo({
        center: [longitude, latitude],
        zoom: targetZoom,
        duration: 400,
        essential: true,
      });
    },
    [
      jobs,
      trackMapClickImpression,
      trackListClickImpression,
      dispatch,
      ref,
      isCenteringRef,
      cancelPendingOpen,
    ]
  );
}

function MobileJobMapMarker({
  job,
  markerId,
  latitude,
  longitude,
  isActive,
  isHovered,
  openJobMarker,
  onSelected,
}: {
  job: MarketplaceJob;
  markerId: string;
  latitude: number;
  longitude: number;
  isActive?: boolean;
  isHovered?: boolean;
  openJobMarker: (options: OpenJobMarkerOptions) => void;
  onSelected: (id: string) => void;
}) {
  const handleActivate = useCallback(() => {
    openJobMarker({
      latitude,
      longitude,
      markerId,
      job,
      trackImpression: true,
      openDetail: false,
    });
    onSelected(markerId);
  }, [openJobMarker, latitude, longitude, markerId, job, onSelected]);

  const handleLabelClick = useCallback(
    (event: MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      handleActivate();
    },
    [handleActivate]
  );

  return (
    <MarketplaceJobMarkerLabel
      isActive={isActive}
      isFavourited={job.isFavourited}
      isHovered={isHovered}
      onClick={handleLabelClick}
      onPointerDown={(event) => {
        event.stopPropagation();
      }}
      title={job.title}
    />
  );
}

function DesktopJobMapMarker({
  job,
  markerId,
  latitude,
  longitude,
  isActive,
  isHovered,
  isOpen,
  openJobMarker,
  onCloseMarker,
  onSelected,
  onClosed,
  zoom,
}: {
  job: MarketplaceJob;
  markerId: string;
  latitude: number;
  longitude: number;
  isActive?: boolean;
  isHovered?: boolean;
  isOpen?: boolean;
  openJobMarker: (options: OpenJobMarkerOptions) => void;
  onCloseMarker: () => void;
  onSelected: (id: string) => void;
  onClosed: () => void;
  zoom?: number;
}) {
  const handleActivate = useCallback(() => {
    openJobMarker({
      latitude,
      longitude,
      markerId,
      job,
      trackImpression: true,
      zoom,
      openDetail: true,
    });
    onSelected(markerId);
  }, [openJobMarker, latitude, longitude, markerId, job, zoom, onSelected]);

  return (
    <MarketplaceJobPopupResponsive
      isActive={isActive}
      isHovered={isHovered}
      isOpen={isOpen}
      job={job}
      onActivate={handleActivate}
      onOpenChange={(open) => {
        if (!open) {
          onCloseMarker();
          onClosed();
        }
      }}
    />
  );
}

function MapMarkers({
  userLocation,
  clusters,
  getClusterLeaves,
  openJobMarker,
  openMarkerId,
  selectedJobMarker,
  onCloseMarker,
  selectedId,
  hoveredId,
  onSelectedIdChange,
  onHoveredIdChange,
}: {
  userLocation: UserLocationState | null | undefined;
  clusters: JobClusterResult[];
  getClusterLeaves: ReturnType<typeof useCluster>["getClusterLeaves"];
  openJobMarker: (options: OpenJobMarkerOptions) => void;
  openMarkerId: string | null;
  selectedJobMarker: SelectedJobMarker | null;
  onCloseMarker: () => void;
  /** Phase 5: list→map selection + hover sync. */
  selectedId?: string | null;
  hoveredId?: string | null;
  onSelectedIdChange?: (id: string | null) => void;
  onHoveredIdChange?: (id: string | null) => void;
}) {
  const isMobile = useIsMobile();
  const visibleLeafIds = useMemo(
    () =>
      new Set(
        clusters.flatMap((cluster) =>
          isJobLeaf(cluster) ? [cluster.properties.id] : []
        )
      ),
    [clusters]
  );

  const selectedClusterLeafMarker =
    selectedJobMarker &&
    openMarkerId === selectedJobMarker.markerId &&
    !visibleLeafIds.has(selectedJobMarker.markerId)
      ? selectedJobMarker
      : null;

  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);

  const openClusterLeaf = useCallback(
    (leaf: JobClusterLeaf) => {
      const [lng, lat] = leaf.geometry.coordinates;
      if (typeof lng === "number" && typeof lat === "number") {
        openJobMarker({
          latitude: lat,
          longitude: lng,
          markerId: leaf.properties.id,
          job: leaf.properties.job,
          trackImpression: true,
          zoom: MAX_MAP_ZOOM,
          openDetail: !isMobile,
        });
      }
    },
    [openJobMarker, isMobile]
  );

  const emitSelected = (id: string) => {
    onSelectedIdChange?.(id);
  };
  const emitClosed = () => {
    onSelectedIdChange?.(null);
  };
  const emitHovered = (id: string | null) => {
    onHoveredIdChange?.(id);
  };

  return (
    <>
      {userLocation?.latitude && userLocation.longitude && (
        <Marker
          latitude={userLocation.latitude}
          longitude={userLocation.longitude}
          style={{ zIndex: hoveredMarkerId === "user-location" ? 50 : 1 }}
        >
          <div
            onPointerEnter={() => setHoveredMarkerId("user-location")}
            onPointerLeave={() => setHoveredMarkerId(null)}
          >
            <span className="relative flex size-3">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-navy-glow opacity-75" />
              <span className="relative inline-flex size-3 rounded-full bg-navy-glow" />
            </span>
          </div>
        </Marker>
      )}

      {/* Render cluster markers */}
      {clusters.flatMap((cluster) => {
        if (!isJobCluster(cluster)) {
          return [];
        }
        const [longitude, latitude] = cluster.geometry.coordinates;

        if (typeof longitude !== "number" || typeof latitude !== "number") {
          return [];
        }

        const clusterId = `cluster-${cluster.properties.cluster_id}`;

        return [
          <Marker
            key={clusterId}
            latitude={latitude}
            longitude={longitude}
            style={{ zIndex: hoveredMarkerId === clusterId ? 50 : 1 }}
          >
            <div
              onPointerEnter={() => setHoveredMarkerId(clusterId)}
              onPointerLeave={() => setHoveredMarkerId(null)}
            >
              <ClusterMarker
                cluster={cluster}
                getClusterLeaves={getClusterLeaves}
                onSelectJob={(leaf) => {
                  openClusterLeaf(leaf);
                  emitSelected(leaf.properties.id);
                }}
              />
            </div>
          </Marker>,
        ];
      })}

      {/* Render individual job markers */}
      {clusters.flatMap((cluster) => {
        if (!isJobLeaf(cluster)) {
          return [];
        }
        const [longitude, latitude] = cluster.geometry.coordinates;
        const job = cluster.properties.job;
        const leafId = cluster.properties.id;

        if (typeof longitude !== "number" || typeof latitude !== "number") {
          return [];
        }

        const markerId = `marker-${leafId}`;

        return [
          <Marker
            key={markerId}
            latitude={latitude}
            longitude={longitude}
            style={{ zIndex: hoveredMarkerId === markerId ? 50 : 1 }}
          >
            <div
              onPointerEnter={() => {
                setHoveredMarkerId(markerId);
                emitHovered(leafId);
              }}
              onPointerLeave={() => {
                setHoveredMarkerId(null);
                emitHovered(null);
              }}
            >
              {isMobile ? (
                <MobileJobMapMarker
                  isActive={leafId === selectedId}
                  isHovered={leafId === hoveredId}
                  job={job}
                  latitude={latitude}
                  longitude={longitude}
                  markerId={leafId}
                  onSelected={emitSelected}
                  openJobMarker={openJobMarker}
                />
              ) : (
                <DesktopJobMapMarker
                  isActive={leafId === selectedId}
                  isHovered={leafId === hoveredId}
                  isOpen={openMarkerId === leafId}
                  job={job}
                  latitude={latitude}
                  longitude={longitude}
                  markerId={leafId}
                  onClosed={emitClosed}
                  onCloseMarker={onCloseMarker}
                  onSelected={emitSelected}
                  openJobMarker={openJobMarker}
                />
              )}
            </div>
          </Marker>,
        ];
      })}

      {selectedClusterLeafMarker && (
        <Marker
          key={`selected-cluster-leaf-${selectedClusterLeafMarker.markerId}`}
          latitude={selectedClusterLeafMarker.latitude}
          longitude={selectedClusterLeafMarker.longitude}
          style={{
            zIndex:
              hoveredMarkerId ===
              `selected-cluster-leaf-${selectedClusterLeafMarker.markerId}`
                ? 50
                : 1,
          }}
        >
          <div
            onPointerEnter={() => {
              setHoveredMarkerId(
                `selected-cluster-leaf-${selectedClusterLeafMarker.markerId}`
              );
              emitHovered(selectedClusterLeafMarker.markerId);
            }}
            onPointerLeave={() => {
              setHoveredMarkerId(null);
              emitHovered(null);
            }}
          >
            {isMobile ? (
              <MobileJobMapMarker
                isActive={selectedClusterLeafMarker.markerId === selectedId}
                isHovered={selectedClusterLeafMarker.markerId === hoveredId}
                job={selectedClusterLeafMarker.job}
                latitude={selectedClusterLeafMarker.latitude}
                longitude={selectedClusterLeafMarker.longitude}
                markerId={selectedClusterLeafMarker.markerId}
                onSelected={emitSelected}
                openJobMarker={openJobMarker}
              />
            ) : (
              <DesktopJobMapMarker
                isActive={selectedClusterLeafMarker.markerId === selectedId}
                isHovered={selectedClusterLeafMarker.markerId === hoveredId}
                isOpen={true}
                job={selectedClusterLeafMarker.job}
                latitude={selectedClusterLeafMarker.latitude}
                longitude={selectedClusterLeafMarker.longitude}
                markerId={selectedClusterLeafMarker.markerId}
                onClosed={emitClosed}
                onCloseMarker={onCloseMarker}
                onSelected={emitSelected}
                openJobMarker={openJobMarker}
                zoom={MAX_MAP_ZOOM}
              />
            )}
          </div>
        </Marker>
      )}
    </>
  );
}

function MapControls({
  mapRef,
  onUserLocationChange,
  writeMapView,
}: {
  mapRef: RefObject<MapRef | null>;
  onUserLocationChange?: (location: {
    latitude?: number;
    longitude?: number;
  }) => void;
  writeMapView: (view: {
    latitude: number;
    longitude: number;
    zoom: number;
  }) => void;
}) {
  const onZoomIn = () => {
    const el = mapRef.current;
    if (el) {
      const map = el.getMap();
      const newZoom = map.getZoom() + 1;
      map.zoomTo(newZoom);
    }
  };

  const onZoomOut = () => {
    const el = mapRef.current;
    if (el) {
      const map = el.getMap();
      const newZoom = map.getZoom() - 1;
      map.zoomTo(newZoom);
    }
  };

  const onFullscreen = () => {
    const el = mapRef.current;
    if (el) {
      const mapContainer = el.getMap().getContainer();
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        mapContainer.requestFullscreen();
      }
    }
  };

  const onGeolocate = () => {
    const el = mapRef.current;
    if (el && navigator.geolocation) {
      const map = el.getMap();

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLatitude = position.coords.latitude;
          const newLongitude = position.coords.longitude;

          onUserLocationChange?.({
            latitude: newLatitude,
            longitude: newLongitude,
          });

          writeMapView({
            latitude: newLatitude,
            longitude: newLongitude,
            zoom: 14,
          });

          map.flyTo({
            center: [newLongitude, newLatitude],
            zoom: 14,
            duration: 400,
            essential: true,
          });
        },
        undefined,
        { enableHighAccuracy: true }
      );
    }
  };

  return (
    <div className="absolute top-6 right-6 z-10 space-y-4">
      <div className="flex flex-col">
        <Button
          className="rounded-b-none border"
          onClick={onZoomIn}
          size="icon"
          variant="secondary"
        >
          <Icons.add className="size-4 p-0" />
        </Button>
        <Button
          className="rounded-none border"
          onClick={onZoomOut}
          size="icon"
          variant="secondary"
        >
          <Icons.remove className="size-4 p-0" />
        </Button>
        <Button
          className="rounded-t-none border"
          onClick={onFullscreen}
          size="icon"
          variant="secondary"
        >
          <Icons.maximize className="size-4 p-0" />
        </Button>
      </div>

      <div className="flex flex-col">
        <Button
          className="border"
          onClick={onGeolocate}
          size="icon"
          variant="secondary"
        >
          <Icons.locate className="size-4 p-0" />
        </Button>
      </div>

      <div className="flex flex-col">
        <Link
          className={cn(
            "border",
            buttonVariants({ variant: "secondary", size: "icon" })
          )}
          href="/"
        >
          <Icons.home className="size-4 p-0" />
        </Link>
      </div>
    </div>
  );
}

function useOfflineMapFallback() {
  const [loaded, setLoaded] = useState(
    () => typeof navigator !== "undefined" && navigator.onLine === false
  );

  useEffect(() => {
    const dismissOverlayIfOffline = () => {
      if (!navigator.onLine) {
        setLoaded(true);
      }
    };

    window.addEventListener("offline", dismissOverlayIfOffline);
    return () => window.removeEventListener("offline", dismissOverlayIfOffline);
  }, []);

  return { loaded, setLoaded };
}

function useRouteLeaveGuard(router: ReturnType<typeof useRouter>) {
  const hasLeftPageRef = useRef(false);

  useEffect(() => {
    const onRouteChangeStart = (url: string) => {
      const targetPath = url.split(URL_QUERY_OR_HASH)[0] ?? url;
      if (targetPath !== "/job-listings") {
        hasLeftPageRef.current = true;
      }
    };
    router.events.on("routeChangeStart", onRouteChangeStart);
    return () => {
      router.events.off("routeChangeStart", onRouteChangeStart);
    };
  }, [router.events]);

  return hasLeftPageRef;
}

function useMapResizeObserver(
  containerRef: RefObject<HTMLDivElement | null>,
  mapRef: RefObject<MapRef | null>
) {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const resizeMap = () => {
      mapRef.current?.getMap().resize();
    };

    const observer = new ResizeObserver(resizeMap);
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [containerRef, mapRef]);
}

export type MarketplaceMapHandle = {
  openSelectedJob: (job: MarketplaceJob) => void;
  closeSelectedMarker: () => void;
};

export const MarketplaceMap = ({
  jobs,
  onBoundsChange,
  onUserLocationChange,
  selectedId,
  onSelectedIdChange,
  hoveredId,
  onHoveredIdChange,
  userLocation,
  ref,
}: ExtendedMapProps & { ref?: Ref<MarketplaceMapHandle> }) => {
  const { theme } = useTheme();
  const router = useRouter();
  const isMobile = useIsMobile();
  const trackMapClickImpression = useTrackJobImpression("map_click");
  const trackListClickImpression = useTrackJobImpression("list_click");

  const [searchParams, setSearchParams] = useQueryStates(
    marketplaceSearchParamsParser
  );

  const { loaded, setLoaded } = useOfflineMapFallback();
  const [mapView, dispatch] = useReducer(mapViewReducer, {
    mapBounds: undefined,
    mapZoom: searchParams.zoom ?? SOUTH_AFRICA_VIEW.zoom,
    openMarkerId: null,
    selectedJobMarker: null,
  });
  const { mapBounds, mapZoom, openMarkerId, selectedJobMarker } = mapView;
  const handleMapError = useMarketplaceMapErrorHandler(setLoaded);
  const hasLeftPageRef = useRouteLeaveGuard(router);
  const isCenteringRef = useRef(false);
  const mapRef = useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useMapResizeObserver(containerRef, mapRef);

  const writeMapView = useCallback(
    (view: { latitude: number; longitude: number; zoom: number }) => {
      if (hasLeftPageRef.current) {
        return;
      }
      // throttleMs: 0 flushes synchronously so a queued write cannot land on a
      // destination route if the user navigates away within the default throttle.
      setSearchParams(view, { throttleMs: 0 });
    },
    [setSearchParams, hasLeftPageRef]
  );

  const onLoad = () => {
    if (mapRef.current) {
      (
        window as unknown as { __marketplaceMapRef: MapRef | null }
      ).__marketplaceMapRef = mapRef.current;
    }

    const el = mapRef.current;
    if (el) {
      const map = el.getMap();
      map.touchZoomRotate.enable();
      map.touchZoomRotate.disableRotation();

      if (!hasViewportParams(router.query)) {
        map.fitBounds(SOUTH_AFRICA_BOUNDS, {
          padding: southAfricaFitPadding(),
          duration: 0,
        });
        const center = map.getCenter();
        const zoom = map.getZoom();
        writeMapView({
          latitude: center.lat,
          longitude: center.lng,
          zoom,
        });
        dispatch({ type: "mapZoom", value: zoom });
      }

      const bounds = readMapBounds(mapRef);
      if (bounds) {
        dispatch({ type: "mapBounds", value: bounds });
        onBoundsChange?.(bounds);
      }
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLatitude = position.coords.latitude;
        const newLongitude = position.coords.longitude;

        onUserLocationChange?.({
          latitude: newLatitude,
          longitude: newLongitude,
        });
      },
      undefined,
      { enableHighAccuracy: true }
    );

    setLoaded(true);
  };

  // Use clustering hook with map bounds
  const { clusters, getClusterLeaves } = useCluster(
    jobs?.map((job) => ({
      id: job.id,
      longitude: job.address.location.coordinates[0] as number,
      latitude: job.address.location.coordinates[1] as number,
      job,
    })),
    mapZoom,
    mapBounds
  );

  const openJobMarker = useOpenJobMarker(
    mapRef,
    jobs,
    trackMapClickImpression,
    {
      dispatch,
      isCenteringRef,
      trackListClickImpression,
    }
  );

  useImperativeHandle(
    ref,
    () => ({
      openSelectedJob: (job: MarketplaceJob) => {
        openJobMarker({
          impressionSource: "list_click",
          latitude: job.address.location.coordinates[1] as number,
          longitude: job.address.location.coordinates[0] as number,
          markerId: job.id,
          job,
          trackImpression: true,
          zoom: MAX_MAP_ZOOM,
          openDetail: !isMobile,
        });
      },
      closeSelectedMarker: () => {
        dispatch({ type: "closeMarker" });
      },
    }),
    [openJobMarker, isMobile]
  );

  const onMoveEndHandler = (evt: ViewStateChangeEvent) => {
    const { viewState } = evt;

    writeMapView({
      latitude: viewState.latitude,
      longitude: viewState.longitude,
      zoom: viewState.zoom,
    });
    dispatch({ type: "mapZoom", value: viewState.zoom });

    // Close any open popover when map is moved, but only if not centering
    if (!isCenteringRef.current) {
      dispatch({ type: "closeMarker" });
      onSelectedIdChange?.(null);
    }

    // Update map bounds for clustering + emit for the listings panel.
    const bounds = readMapBounds(mapRef);
    if (bounds) {
      dispatch({ type: "mapBounds", value: bounds });
      onBoundsChange?.(bounds);
    }
  };

  return (
    <div className="relative size-full min-h-0" ref={containerRef}>
      <ReactMapGL
        boxZoom={true}
        doubleClickZoom={true}
        dragPan={true}
        dragRotate={false}
        id="MarketplaceMap"
        initialViewState={{
          latitude: searchParams.latitude ?? SOUTH_AFRICA_VIEW.latitude,
          longitude: searchParams.longitude ?? SOUTH_AFRICA_VIEW.longitude,
          zoom: searchParams.zoom ?? SOUTH_AFRICA_VIEW.zoom,
          pitch: 0,
          bearing: 0,
        }}
        keyboard={true}
        // react-doctor-disable-next-line react-doctor/public-env-secret-name -- Mapbox public access token (pk.*) is designed to be exposed in the browser bundle
        mapboxAccessToken={env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN}
        mapStyle={
          theme === "dark"
            ? env.NEXT_PUBLIC_MAPBOX_STYLE_MONOCHROME_DARK
            : env.NEXT_PUBLIC_MAPBOX_STYLE_MONOCHROME_LIGHT
        }
        maxPitch={0}
        maxZoom={MAX_MAP_ZOOM}
        minPitch={0}
        minZoom={0}
        onClick={() => {
          if (!isCenteringRef.current) {
            dispatch({ type: "closeMarker" });
            onSelectedIdChange?.(null);
          }
        }}
        onError={handleMapError}
        onLoad={onLoad}
        onMoveEnd={onMoveEndHandler}
        projection={{ name: "mercator" }}
        ref={mapRef}
        reuseMaps={true}
        scrollZoom={true}
        style={{ height: "100%", width: "100%" }}
        touchPitch={false}
        touchZoomRotate={true}
      >
        <MapMarkers
          clusters={clusters}
          getClusterLeaves={getClusterLeaves}
          hoveredId={hoveredId}
          onCloseMarker={() => dispatch({ type: "closeMarker" })}
          onHoveredIdChange={onHoveredIdChange}
          onSelectedIdChange={onSelectedIdChange}
          openJobMarker={openJobMarker}
          openMarkerId={openMarkerId}
          selectedId={selectedId}
          selectedJobMarker={selectedJobMarker}
          userLocation={userLocation}
        />
        {!isMobile && (
          <MapControls
            mapRef={mapRef}
            onUserLocationChange={onUserLocationChange}
            writeMapView={writeMapView}
          />
        )}
      </ReactMapGL>
      <div
        className={cn(
          "transition-all duration-300 ease-out",
          "inset-0 z-10 flex flex-1 items-center justify-center bg-background",
          loaded ? "hidden opacity-0" : "absolute opacity-100"
        )}
      >
        <Loading />
      </div>
    </div>
  );
};
